// src/components/layout/BenchmarkWatermark.tsx
//
// Page-wide BENCHMARK AV watermark. The actual brand mark image
// (public/benchmark-logo-mark.png, sourced from
// wiki/raw/assets/benchmark-av-be/logo.png) is layered behind page
// content on every route so the brand is recognizable even when no
// hero is on screen.
//
// The PNG has a transparent background but dark-on-white mark. On
// our dark canvas we apply a CSS invert() so it reads as a soft
// light silhouette. `mix-blend-mode: screen` would also work — we
// stay with opacity to remain colour-token-friendly.
//
// Two layers:
//   1. Bottom-right logo silhouette anchored to the viewport. Large
//      enough to read as a brand mark on a 1440-wide viewport.
//   2. A faint vertical wordmark streak on the left edge so the brand
//      is visible even on short pages.
//
// Pure decoration — `pointer-events-none`, `aria-hidden`, opacity tuned
// so foreground text always wins WCAG contrast.

import { cn } from "@/lib/cn";

interface BenchmarkWatermarkProps {
  /** Override intensity for dense pages (admin/portal sidebars). */
  intensity?: "subtle" | "normal";
}

const LOGO_URL = "/benchmark-logo-mark.png";

/**
 * The page-wide watermark. Should be rendered once per layout, as a
 * sibling of the main content area, with `pointer-events-none` and
 * `aria-hidden` so it never traps focus or screen-reads.
 */
export function BenchmarkWatermark({
  intensity = "normal",
}: BenchmarkWatermarkProps) {
  // The PNG is dark on transparent; we invert to a soft light/teal
  // silhouette on the dark canvas. Opacity controls how loud the
  // mark reads against foreground content.
  const overlayOpacity = intensity === "subtle" ? "opacity-15" : "opacity-25";
  const invertFilter =
    "brightness(0) saturate(100%) invert(1)"; // pure light silhouette
  const sidebarInvert =
    "brightness(0) saturate(100%) invert(67%) sepia(56%) " +
    "saturate(475%) hue-rotate(132deg) brightness(89%) contrast(91%)"; // teal

  return (
    <div
      aria-hidden
      className={cn(
        "pointer-events-none fixed inset-0 z-0 overflow-hidden",
        overlayOpacity,
      )}
      data-bench-watermark={intensity}
    >
      {/* Bottom-right logo silhouette (real brand mark) — white silhouette */}
      <img
        src={LOGO_URL}
        alt=""
        draggable={false}
        style={{ filter: invertFilter }}
        className="absolute bottom-6 right-4 w-[440px] max-w-[42vw] select-none"
      />

      {/* Left-edge ghost mark, with teal tint to keep the BENCHMARK brand
          teal visible on the dark plane. */}
      <img
        src={LOGO_URL}
        alt=""
        draggable={false}
        style={{ filter: sidebarInvert }}
        className="absolute -left-12 top-1/3 w-[320px] max-w-[26vw] -rotate-12 select-none opacity-40"
      />
    </div>
  );
}
